
import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import MaterialReactTable from 'material-react-table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faTrash, faCalendar, faFileDownload } from '@fortawesome/free-solid-svg-icons';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import { DateRangePicker } from 'react-date-range';
import moment from 'moment'
import AsyncSelect from 'react-select/async';
import Select, { SelectInstance } from 'react-select';
import SAlert from '../../shared/SAlert';
import useUtils from '../../../../utiles/useUtiles';
import Footer from '../../../components/Footer';
import { IMPORTED_CSV_STATUS } from '../../../../utiles/constants'
import { format } from 'date-fns';

const importListStatus = Object.keys(IMPORTED_CSV_STATUS).map((e: string) => ({
  label: e,
  value: IMPORTED_CSV_STATUS[e as keyof typeof IMPORTED_CSV_STATUS]
}))

const ImportList: React.FC = () => {

  const selectNetworkTypeRef = useRef<SelectInstance>(null);
  const selectNetworkRef = useRef<SelectInstance>(null);
  const inputDateRef = useRef<HTMLInputElement>(null);
  const selectImportStatusRef = useRef<SelectInstance>(null);
  const calendarRef = useRef<HTMLInputElement>(null);
  // const navigate = useNavigate();
  const [urlParams, setUrlParams] = useSearchParams();
  const [importList, setImportList] = useState<any>( { count: 0, rows: [] } );
  const [networkList, setNetworkList] = useState<any[]>( [] );
  const [searchParams, setSearchParams] = useState<any>({
     searchText: '' ,
     status: urlParams.get('status') && {label: '', value: urlParams.get('status')} || null,
     csvType: urlParams.get('csvTypeId') && {label: '', value: urlParams.get('csvTypeId')} || null,
     network: urlParams.get('networkId') && {label: '', value: urlParams.get('networkId')} || null,
     startDate: urlParams.get('startDate') && moment(urlParams.get('startDate')).format("YYYY-MM-DD") || moment().subtract(6, 'days').format('YYYY-MM-DD'),
     endDate: urlParams.get('endDate') && moment(urlParams.get('endDate')).format("YYYY-MM-DD") || moment().format('YYYY-MM-DD'),
  });
  const [loading, setLoading] = useState<boolean>( false );
  const [selectedRecord, setSelectedRecord] = useState<any>( null );
  const [showDeleteAlert, setShowDeleteAlert] = useState<boolean>( false );
  const [showCalendar, setShowCalendar] = useState<boolean>(false);
  const [focusedRange, setFocusedRange] = useState<any>([0, 0]);
  const [selectedRange, setSelectedRange] = useState<any>([
    {
      startDate: moment().subtract(6, 'days').toDate(),
      endDate: moment().toDate(),
      key: 'selection',
    },
  ]);
  const [pagination, setPagination] = useState<any>( {
    pageIndex: 0,
    pageSize: 10
  } );
  const { axiosRequest, baseUrl, toastInstance , loadingScreen} = useUtils()
  const axios = axiosRequest();
  const toast = toastInstance();

  const statusUrlParam: string | null = urlParams.get('status');
  const startDateUrlParam: string | null = urlParams.get('startDate');
  const endDateUrlParam: string | null = urlParams.get('endDate');
  const csvTypeIdUrlParam: string | null = urlParams.get('csvTypeId');
  const networkIdUrlParam: string | null = urlParams.get('networkId');

  useEffect( () => {
    if ( csvTypeIdUrlParam ) {
      fetchCsvTypesQueryString( csvTypeIdUrlParam )
    }
    if ( urlParams.get('status') ) {
      const statusList = importListStatus.filter(e => statusUrlParam && e['value'] == +statusUrlParam)
      selectImportStatusRef.current && selectImportStatusRef.current.setValue(statusList, 'select-option')
    }

    setSelectedRange([{
        startDate: startDateUrlParam && moment(startDateUrlParam).toDate() || moment().subtract(6, 'days').toDate(),
        endDate: endDateUrlParam && moment(endDateUrlParam).toDate() || moment().toDate(),
        key: 'selection',
    }])

    fetchImportedList();

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [] );

  useEffect( () => {
    fetchImportedList();
  }, [ pagination ] );

  useEffect( () => {
    if ( searchParams['csvType'] ) {
        selectNetworkRef.current && selectNetworkRef.current.clearValue();
        fetchNetwork( '', searchParams['csvType']['value'] );
    }
  }, [ searchParams['csvType'] ] );

  useEffect(() => {
    setSearchParams({
      ...searchParams,
      startDate: selectedRange[0]['startDate'] && moment(selectedRange[0]['startDate']).format("YYYY-MM-DD") || null,
      endDate: selectedRange[0]['endDate'] && moment(selectedRange[0]['endDate']).format("YYYY-MM-DD") || null
    })
  }, [selectedRange])

  const fetchImportedList = async () => {
    setLoading( true );

    const queryString = [];
    if ( searchParams['status'] != null ) {
      queryString.push(`status=${searchParams['status']['value']}`)
    }
    if ( searchParams['csvType'] ) {
      queryString.push(`csvTypeId=${searchParams['csvType']['value']}`)
    }
    if ( searchParams['network'] ) {
      queryString.push(`networkId=${searchParams['network']['value']}`)
    }
    if ( searchParams['startDate'] ) {
      queryString.push(`startDate=${searchParams['startDate']}`)
    }
    if ( searchParams['endDate'] ) {
      queryString.push(`endDate=${searchParams['endDate']}`)
    }

		const request = await axios.get( `${baseUrl()}/import_csv?page=${pagination.pageIndex}&pageSize=${pagination.pageSize}&${queryString.join('&')}` );
    setLoading( false );
    const { error, result, message } = request.data;

		if ( error ) {
			toast.error( message );
		} else {
			setImportList( result );
		}
  };

  const fetchCsvTypesQueryString = async ( id: string ) => {
    const result = await fetchCsvTypes( { id } );
    if ( result.length ) {
      selectNetworkTypeRef.current && selectNetworkTypeRef.current.setValue(result[0], 'select-option')
    }

    if ( networkIdUrlParam ) {
      fetchNetworkQueryString(networkIdUrlParam, id)
    }
  }

  const fetchNetworkQueryString = async ( id: string, csvTypeId: string ) => {
    const result = await fetchNetwork({ id }, csvTypeId );
    if ( result.length ) {
      selectNetworkRef.current && selectNetworkRef.current.setValue(result[0], 'select-option')
    }
  }

  const csvTypeOptions = ( inputValue: string ): Promise<any[]> => {
      return new Promise<any[]>( async ( resolve ) => {
        const result: any[] = await fetchCsvTypes( {searchText: inputValue} );
        resolve( result );
      });
  }
    
	const fetchCsvTypes = ( searchParams: any ): Promise<any[]> => {
      return new Promise<any[]>( async ( resolve ) => {
          const queryString = Object.keys(searchParams).map(key => `${key}=${searchParams[key]}`)
          const request = await axios.get( `${baseUrl()}/template_types?page=0&pageSize=5&${queryString.join('&')}` );
          const { error, result, message } = request.data;

          if ( error ) {
              toast.error( message );
              resolve( [] );
          } else {
              const data = result['rows'].map( (r: any) => ( {
                  label: r.name,
                  value: r.id 
              } ) );
              resolve( data );
          }
      });
	};

  const onChangeNetwork = ( searchText: string ) => {
		if ( searchParams['csvType'] ) {
			fetchNetwork({searchText}, searchParams['csvType']['value']);
		}
	}

  const networkOptions = ( inputValue: string ): Promise<any[]> => {
    return new Promise<any[]>( async ( resolve ) => {
      if ( searchParams['csvType'] ) {
        const result = await fetchNetwork( {searchText: inputValue}, searchParams['csvType']['value'] );
        resolve( result );
      } else {
        resolve( [] );
      }
    });
  }
  const fetchNetwork = ( searchParams: any, templateType: string ): Promise<any[]> => {
      return new Promise<any[]>( async ( resolve ) => {
          var query = '';
          if ( searchParams['searchText'] ) {
              query += `&searchText=${searchParams['searchText']}`;
          } if ( searchParams['id'] ) {
            query += `&id=${searchParams['id']}`;
          } if ( templateType ) {
              query += `&csvTypeId=${templateType}`;
          }
          const request = await axios.get( `${baseUrl()}/template_type_networks?page=0&pageSize=5${query}` );
          const { error, result, message } = request.data;

          if ( error ) {
              toast.error( message );
              setNetworkList([]);
              resolve( [] );
          } else {
              const data = result['rows'].map( (r: any) => ( {
                  label: r.name,
                  value: r.id 
              } ) );
              setNetworkList( data );
              resolve( data );
          }
      } );
  };

  // const onSearchChange = ( field, value ) => {
  //   console.log( 'field: ', field, ' value: ', value );
  //   setSearchParams( {
  //     ...searchParams,
  //     [field]: value
  //   } );
  // };

  const resetFilter = () => {
    selectNetworkTypeRef.current && selectNetworkTypeRef.current.clearValue();
    selectNetworkRef.current && selectNetworkRef.current.clearValue();
    selectImportStatusRef.current && selectImportStatusRef.current.clearValue();
    setSelectedRange([{
        startDate: null,
        endDate: null,
        key: 'selection',
    }])
  }

  const confirmDelete = ( record: any ) => {
    setSelectedRecord( record );
    setShowDeleteAlert( true );
  };

  const handleDelete = async () => {
    setLoading( true );
		const request = await axios.delete( `${baseUrl()}/import_csv/${selectedRecord.id}` );
    const { error, result, message } = request.data;

    setShowDeleteAlert( false );
    setLoading( false );
    
		if ( error ) {
			toast.error( message || 'Error to delete the record' );
		} else {
      toast.success( message );
      fetchImportedList();
      setSelectedRecord( null );
		}
  };

  const downloadFile = async ( filepath: string ) => {
    const fileName = filepath.split( '/' ).pop() || '';
    const { data } = await axios.get( `${baseUrl()}/${filepath}`, { responseType: "blob" } );

    const url = URL.createObjectURL( new Blob( [data] ) );
    const link = document.createElement( 'a' );
    link.href = url;
    link.setAttribute( 'download', fileName );

    // Append to html link element page
    document.body.appendChild( link );

    // Start download
    link.click();

    // Clean up and remove the link
    link.parentNode && link.parentNode.removeChild( link );
  };

  const handleCalendarToggle = () => {
    setShowCalendar(!showCalendar);
  };

  const handleSelect = (ranges: any) => {
    setSelectedRange([ranges.selection]);
    if ( focusedRange[1] ) {
      setShowCalendar(false);
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (calendarRef.current && !calendarRef.current.contains(event.target as HTMLElement)) {
      setShowCalendar(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'template',
        header: 'Template',
        minSize: 100,
        maxSize: 150,
        Cell: ( { cell }: any ) => <div>
          <b>{cell.getValue()['name']}</b> <br />
          <span>{cell.row.original['csvType'] && cell.row.original['csvType']['name'] || 'NA'}</span> <br />
          <span>{cell.row.original['network'] && cell.row.original['network']['name'] || 'NA'}</span>
        </div>,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        minSize: 10,
        maxSize: 10,
        Cell: ( { cell } ) => <Stack direction="row" spacing={1}>{
          cell.getValue() == '0' ? <Chip label='Pending' color="primary" /> :
          cell.getValue() == '1' ? <Chip label='In Progress' color="warning" /> :
          cell.getValue() == '2' ? <Chip label='Completed' color="success" /> : <Chip label='Failed' color="error" />
        }</Stack>,
      },
      {
        accessorKey: 'errorMsg',
        header: 'Error',
        minSize: 60,
        maxSize: 90,
        Cell: ( { cell } ) => <span title={cell.getValue() && cell.getValue() || ''}>{
          cell.getValue() || 'NA'
        }</span>,
      },
      {
        accessorKey: 'createdAt',
        header: 'Created At',
        minSize: 30,
        maxSize: 50,
        Cell: ( { cell } ) => <span>{
          format( new Date( cell.getValue() ), 'yyyy-MM-dd HH:mm:ss' )}</span>,
      },
      {
        accessorKey: 'actions',
        header: 'Action',
        minSize: 5,
        maxSize: 10,
        Cell: ( { cell } ) => <div>
            {/* {
              cell.row.original['outputFilePath'] && (
                <FontAwesomeIcon
                  title='Download .json File'
                  icon={faDownload}
                  className="isPointer ml-2 text-success cursor-pointer"
                  onClick={() => {
                    downloadFile( cell.row.original['outputFilePath'] );
                  }}
                />
              )
            } */}
            {
              cell.row.original['path'] && (
                <FontAwesomeIcon
                  title='Download .csv File'
                  icon={faFileDownload}
                  className="isPointer ml-2 text-success cursor-pointer"
                  onClick={() => {
                    downloadFile( cell.row.original['path'] );
                  }}
                />
              )
            }
            <FontAwesomeIcon
              title='Remove'
              icon={faTrash}
              className="isPointer ml-2 text-danger cursor-pointer"
              onClick={() => confirmDelete( cell.row.original )}
            />
        </div>,
      }
    ],
    [],
  );

  const staticRanges = [
    {
      label: 'Today',
      range: () => ({
        startDate: moment().toDate(),
        endDate: moment().toDate(),
      }),
      isSelected: (dateRange: any) => {
        const today = moment().toDate();
        return (
          dateRange.startDate.toDateString() === today.toDateString() &&
          dateRange.endDate.toDateString() === today.toDateString()
        );
      },
    },
    {
      label: 'Yesterday',
      range: () => {
        const yesterday = moment().subtract(1, 'days').toDate();
        return {
          startDate: yesterday,
          endDate: yesterday,
        };
      },
      isSelected: (dateRange: any) => {
        const yesterday = moment().subtract(1, 'days').toDate();
        return (
          dateRange.startDate.toDateString() === yesterday.toDateString() &&
          dateRange.endDate.toDateString() === yesterday.toDateString()
        );
      },
    },
    {
      label: 'Last 7 Days',
      range: () => ({
        startDate: moment().subtract(6, 'days').toDate(),
        endDate: moment().toDate(),
      }),
      isSelected: (dateRange: any) => {
        const last_7_days = moment().subtract(6, 'days').toDate();
        const today = moment().toDate();
        return moment(dateRange.endDate).diff(today, 'days') == 0 && moment(dateRange.startDate).diff(last_7_days, 'days') == 0;
      },
    },
    {
      label: 'Last 30 Days',
      range: () => ({
        startDate: moment().subtract(29, 'days').toDate(),
        endDate: moment().toDate(),
      }),
      isSelected: (dateRange: any) => {
        const last_30_days = moment().subtract(29, 'days').toDate();
        const today = moment().toDate();
        return moment(dateRange.endDate).diff(today, 'days') == 0 && moment(dateRange.startDate).diff(last_30_days, 'days') == 0;
      },
    },
    // Add more static ranges as needed
  ];

  return (
    <>
        <div className="container-fluid">
            <div className="row">
                <div className="col-sm-12">
                    <div className="main-container">
                      <div className="main-container-inner">
                        <div className="main-card revrex-data-table-card">
                            <div className='d-flex justify-content-between align-items-center mb-3'>
                                <h1 className='page-title-sm'>Imported List</h1>
                                {/* <button className='btn btn-md btn-custom-secondary' onClick={() => navigate('/csv-tool/import-csv')}>
                                    Import CSV
                                </button> */}
                            </div>
                            <div className='mb-4 row gap-10'>
                                <div className='col-md-5 select-dropdown'>
                                    <AsyncSelect 
                                        ref={selectNetworkTypeRef}
                                        placeholder="Select template type"
                                        cacheOptions 
                                        loadOptions={csvTypeOptions} 
                                        defaultOptions
                                        onChange={selectedOption => {
                                            setSearchParams((preValue: any) => ({
                                              ...preValue,
                                              network: null,
                                              csvType: selectedOption
                                            }));
                                            setNetworkList([]);
                                        }} 
                                    />
                                </div>
                                <div className='col-md-4 select-dropdown'>
                                    <AsyncSelect 
                                        ref={selectNetworkRef}
                                        placeholder="Select network"
                                        loadOptions={networkOptions} 
                                        defaultOptions={networkList}
                                        onInputChange={onChangeNetwork}
                                        onChange={selectedOption => {
                                            setSearchParams((preValue: any) => ({
                                              ...preValue,
                                              network: selectedOption
                                            }));
                                        }} 
                                    />
                                </div>
                                <div className='col-md-1 d-flex'>
                                    <button type="button" className="btn btn-md btn-custom-secondary" onClick={fetchImportedList}>Search</button> &nbsp;
                                    <button type="button" className="btn btn-md btn-custom-secondary" onClick={resetFilter}>Reset</button>
                                </div>
                                <div className="date-picker-container col-md-5" ref={calendarRef}>
                                  <div>
                                    <input
                                      ref={inputDateRef}
                                      className='form-control'
                                      type="text"
                                      value={ selectedRange[0].startDate && selectedRange[0].endDate && `${moment(selectedRange[0].startDate).format('MM/DD/YYYY')} - ${moment(selectedRange[0].endDate).format('MM/DD/YYYY')}` || ''}
                                      placeholder='Start Date - End Date'
                                      readOnly
                                    />
                                    <span className='pass-field-icon position-absolute' style={{right: '24px'}} onClick={handleCalendarToggle}>
                                      <FontAwesomeIcon
                                      color='#978f8f'
                                      icon={faCalendar}
                                      className="iconGray isPointer ml-2"
                                      />
                                    </span>
                                  </div>
                                  
                                  {
                                      showCalendar && (
                                        <div className="calendar-overlay">
                                          <DateRangePicker
                                            maxDate={new Date()}
                                            focusedRange={focusedRange}
                                            onRangeFocusChange={setFocusedRange}
                                            onChange={item => handleSelect(item)}
                                            months={2}
                                            ranges={selectedRange}
                                            staticRanges={staticRanges}
                                            direction="horizontal"
                                            showDateDisplay={true}
                                            dateDisplayFormat="MM/dd/yyyy"
                                          />
                                        </div>
                                      )
                                  }
                                </div>
                                <div className='col-md-4 select-dropdown' style={{zIndex: 1}}>
                                    <Select
                                      ref={selectImportStatusRef}
                                      placeholder="Select status"
                                      isSearchable={true}
                                      options={importListStatus}
                                      onChange={selectedOption => {
                                          setSearchParams((preValue: any) => ({
                                            ...preValue,
                                            status: selectedOption
                                          }));
                                      }}
                                    />
                                </div>
                                
                                {/* <div className='flex-grow-1'>
                                  <input type={"text"} name="searchText" placeholder="CSV name" className="form-control" autoComplete='new-password' onChange={( e ) => onSearchChange( 'searchText', e.target.value )} />
                                </div> */}
                                {/* <div className=''>
                                  <button type="button" className="btn btn-md btn-custom-secondary" onClick={fetchImportedList}>Search</button>
                                </div> */}
                            </div>
                            <div className='template-list-table' style={{zIndex: 0, position: 'sticky'}}>
                                <MaterialReactTable 
                                    columns={columns} 
                                    data={importList?.rows} 
                                    rowCount={importList?.count} 
                                    enableColumnOrdering={false} 
                                    enableRowSelection={false} 
                                    enableSelectAll={false}
                                    enableColumnActions={false}
                                    manualPagination
                                    onPaginationChange={setPagination}
                                    state={{ pagination }}
                                    enableSorting={false}
                                    enableGlobalFilter={true}
                                    enableFullScreenToggle={false}
                                    enableDensityToggle={false}
                                    enableHiding={false}
                                    enableColumnFilters
                                    enableFilters={false}
                                />
                            </div>
                        </div>
                      </div>
                      <Footer />
                    </div>
                </div>
            </div>
            <SAlert 
            msg={'Are you sure to delete the record?'} 
            show={showDeleteAlert} 
            title={'Delete record'} 
            onConfirm={handleDelete}
            showCancel={true}
            onCancel={() => setShowDeleteAlert( false )} 
            />
        </div>
        {loadingScreen( loading )}
    </>
  );
}

export default ImportList